import React, { useEffect, useState, useMemo } from "react";
import { formatNumber } from "../../../utils/format";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Box,
  Grid,
  Modal,
  IconButton,
  Button,
  Tooltip,
  Menu,
  Chip,
  MenuItem,
  Autocomplete,
  TextField,
  AlertTitle,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Typography,
  SvgIcon,
  Snackbar,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import moment from "moment";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../../services/api_service";
import {
  fetchProductReportsAPI,
  fetchReportsAPI,
  productInStockAPI,
  productAvailabilityAPI,
  stockTransferDataAPI,
  storesDropdownAPI,
  storeWiseProductReportAPI,
} from "../../../config";
import Layout from "../../../layout/Layout";
import { dateColumnFilterProps } from "../../../components/MrtDateColumnFilter";
import dayjs from "dayjs";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import ReservedStockDetailsModal from "../../../components/ReservedStockDetailsModal";
import BatchPriceEditModal from "../../../components/BatchPriceEditModal";
import BatchSplitModal from "../../../components/BatchSplitModal";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { buildExportFileName } from "../../../utils/exportFileName";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

const ProductReports = () => {
  const [tblList, setTblList] = useState([]);
  const params = useParams();
  const { storeId } = params;

  const [store, setStore] = useState(storeId);
  const [summary, setSummary] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });
  const location = useLocation();

  const [message, setMessage] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [rowCount, setRowCount] = useState(0);

  const [storeOptions, setStoreOptions] = useState([]);
  const [storeName, setStoreName] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [reservedDetail, setReservedDetail] = useState(null); // { product_id, product_name }
  const [priceEditDetail, setPriceEditDetail] = useState(null); // { product_id, product_name }
  const [batchDetail, setBatchDetail] = useState(null); // { product_id, product_name }

  const handleClear = () => {
    fetchData();
  };

  const handleSearch = () => {
    fetchData(fromDate, toDate);
  };

  const fetchStores = async () => {
    try {
      const result = await apiData("get", storesDropdownAPI);
      if (result.success) {
        setStoreOptions(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      setMessage(null);
    }, 3000);
  }, [message]);

  // Sellable quantity of a row: the merged-in availability, or on-hand minus
  // reserved and missing — never negative.
  const rowAvailable = (r) =>
    Math.max(
      0,
      Number(
        r.available ??
          Number(r.instock || 0) - Number(r.reserved || 0) - Number(r.missing || 0),
      ),
    );

  // Overlay Reserved + Available (ATP) per product for this store. Rows here are
  // one-per-product, so the merge is exact (no batch duplication). Reuses the
  // batch-availability endpoint — no backend change needed.
  const mergeAvailability = async (rows) => {
    if (!storeId || !rows?.length) return rows;
    try {
      const productIds = [
        ...new Set(rows.map((r) => r.product_id).filter(Boolean)),
      ];
      if (!productIds.length) return rows;
      const avail = await apiData("post", productAvailabilityAPI, {
        store_id: storeId,
        product_ids: productIds,
      });
      if (avail.success && Array.isArray(avail.data)) {
        const map = {};
        avail.data.forEach((a) => {
          map[a.product_id] = a;
        });
        return rows.map((r) => {
          const a = map[r.product_id];
          return a
            ? {
                ...r,
                reserved: a.reserved,
                missing: a.missing,
                // Units past their expiry date are in "In Stock" but not sellable.
                expired: Math.max(0, Number(r.instock || 0) - Number(a.onHand || 0)),
                available: Math.max(0, a.atp),
              }
            : { ...r, reserved: 0, available: r.instock };
        });
      }
    } catch (e) {
      console.error("Failed to fetch reserved/ATP:", e);
    }
    return rows;
  };

  const handleExportData = async () => {
    try {
      setLoading(true);

      const payload = {
        // Always from the first page: a later page index would skip rows (and
        // return nothing from page 2 on).
        pagination: JSON.stringify({
          pageIndex: 0,
          pageSize: 10000000,
        }),
        sorting: sorting.length > 0 ? JSON.stringify(sorting) : null,
        search: columnFilters.length > 0 ? JSON.stringify(columnFilters) : null,
      };

      const response = await apiData(
        "get",
        `${productInStockAPI}?store_id=${storeId}`,
        payload
      );

      if (!response?.data?.dataRows || response.data?.dataRows.length === 0) {
        alert("No data to export!");
        return;
      }

      const rows = await mergeAvailability(response.data.dataRows);
      const exportData = rows.map((item, index) => ({
        // Same columns, same order as the table on screen
        "#": index + 1,
        "Product Name": item.product_name || "NA",
        "SKU Code": item.sku || "NA",
        "First Stock Entry Date":
          moment(item.created_at).format("DD-MM-YYYY") || "NA",
        "Last Stock Added Date":
          moment(item.last_added_at).format("DD-MM-YYYY") || "NA",
        "In Stock": item.instock || 0,
        Reserved: item.reserved ?? 0,
        Damaged: item.damaged ?? 0,
        Missing: item.missing ?? 0,
        // Same value the Available cell shows (never negative)
        Available: rowAvailable(item),
        Expired: Number(item.expired || 0),
      }));
      // TOTAL row — use the backend all-data summary (entire filtered set), the
      // same source the on-screen footer uses, so both match and neither is
      // limited to the current page.
      const s = response.data?.summary || {};
      exportData.push({
        "#": "",
        "Product Name": "TOTAL",
        "In Stock": Number(s.total_quantity) || 0,
        Reserved: Number(s.total_reserved) || 0,
        Damaged: Number(s.total_damaged) || 0,
        Missing: Number(s.total_missing) || 0,
        Available: Number(s.total_available) || 0,
        Expired: Number(s.total_expired) || 0,
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Product_Report");

      const fileName = buildExportFileName("Product_Report", [
        { label: "Store", value: storeName || storeId },
      ]);
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  async function fetchData() {
    try {
      setLoading(true);
      const payload = {
        pagination: JSON.stringify({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        }),
        sorting: sorting.length > 0 ? JSON.stringify(sorting) : null,
        search: columnFilters.length > 0 ? JSON.stringify(columnFilters) : null,
        // fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
        // toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
      };
      // const response = await apiData(
      //   "get",
      //   `${fetchReportsAPI}/${storeId}`,
      //   payload
      // );

      const response = await apiData(
        "get",
        `${productInStockAPI}?store_id=${storeId}`,
        payload
      );
      if (response.success && response.data.dataRows) {
        setStoreName(response.data.dataRows[0]?.store_name || "");

        setRowCount(response.data?.totalCount);
        const rows = await mergeAvailability(response.data.dataRows);
        setTblList(rows);
        setSummary(response.data?.summary);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [globalFilter, columnFilters, sorting, pagination, store]);

  const Innercolumns = useMemo(
    () => [
      {
        accessorKey: "product_name",
        header: "Product Name",
        size: 10,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="View Product Details" arrow>
              <Button
                variant="text"
                onClick={() => {
                  navigate("/reports/product-details", {
                    state: {
                      product_name: row.original.product_name,
                      product_id: row.original.product_id,
                      store_id: storeId,
                      store_name: storeName,
                      in_stock: row.original.instock,
                      uom: row.original.uom_name,
                      sku: row.original.sku,
                    },
                  });
                }}
                sx={{ textTransform: "none" }}
              >
                {row.original.product_name}
              </Button>
            </Tooltip>
            <Tooltip title="Edit stock batch price" arrow>
              <IconButton
                size="small"
                onClick={() =>
                  setPriceEditDetail({
                    product_id: row.original.product_id,
                    product_name: row.original.product_name,
                  })
                }
              >
                <EditOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        // Footer: () => `Total`,
      },
      {
        accessorKey: "sku",
        header: "SKU Code",
        size: 10,
      },
      {
        accessorKey: "created_at",
        header: "First Stock Entry Date",
        ...dateColumnFilterProps(),
        Cell: ({ row }) => moment(row.original.created_at).format("DD-MM-YYYY"),
        size: 10,
      },
      {
        accessorKey: "last_added_at",
        header: "Last Stock Added Date",
        ...dateColumnFilterProps(),
        Cell: ({ row }) =>
          moment(row.original.last_added_at).format("DD-MM-YYYY"),

        size: 10,
        Footer: () => <Typography fontWeight={600}>TOTAL</Typography>,
      },
      {
        accessorKey: "instock",
        header: "In Stock",
        size: 10,
        Cell: ({ row }) => {
          const v = Number(row.original.instock || 0);
          if (v <= 0) {
            return (
              <Typography fontSize="inherit" sx={{ fontWeight: 400 }}>
                {formatNumber(v)}
              </Typography>
            );
          }
          // Click to see the batch breakdown (each manufacture date / expiry /
          // quantity) and how much is reserved vs free to deduct.
          return (
            <Tooltip title="Click to see the batch split (and reserved vs free)" arrow>
              <Typography
                fontSize="inherit"
                onClick={() =>
                  setBatchDetail({
                    product_id: row.original.product_id,
                    product_name: row.original.product_name,
                  })
                }
                sx={{
                  fontWeight: 600,
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {formatNumber(v)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () =>
          summary?.total_quantity ? (
            <Typography fontWeight={600}>{formatNumber(summary.total_quantity)}</Typography>
          ) : null,
      },
      {
        accessorKey: "reserved",
        header: "Reserved",
        size: 10,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const r = Number(row.original.reserved || 0);
          if (r <= 0) {
            return (
              <Typography fontSize="inherit" sx={{ fontWeight: 400 }}>
                {formatNumber(r)}
              </Typography>
            );
          }
          return (
            <Tooltip title="Click to see which CDN(s)/salesman are holding this" arrow>
              <Typography
                fontSize="inherit"
                onClick={() =>
                  setReservedDetail({
                    product_id: row.original.product_id,
                    product_name: row.original.product_name,
                  })
                }
                sx={{
                  fontWeight: 600,
                  color: "#c62828",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {formatNumber(r)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () =>
          summary?.total_reserved ? (
            <Typography fontWeight={600}>{formatNumber(summary.total_reserved)}</Typography>
          ) : null,
      },
      {
        accessorKey: "available",
        header: "Available",
        size: 10,
        enableColumnFilter: false,
        // Calculated, not a raw stored field: only trust a merged-in
        // `available` (from mergeAvailability's ATP lookup) when present,
        // otherwise derive it as on-hand minus reserved rather than
        // falling back to raw in-stock (which would ignore reservations).
        Cell: ({ row }) => formatNumber(rowAvailable(row.original)),
        // Backend all-data summary (entire filtered set, not just the page), so
        // the list footer matches the Excel footer.
        Footer: () => (
          <Typography fontWeight={600}>
            {formatNumber(Number(summary?.total_available) || 0)}
          </Typography>
        ),
      },
      {
        accessorKey: "damaged",
        header: "Damaged",
        size: 10,
        enableColumnFilter: false,
        // Non-sellable damaged bucket (returned-damaged units). Kept separate
        // from In Stock so it is never sold.
        Cell: ({ row }) => {
          const d = Number(row.original.damaged || 0);
          return (
            <Tooltip title={d > 0 ? "Click to view damaged products" : ""} arrow>
              <Typography
                fontSize="inherit"
                onClick={() => {
                  if (d > 0) {
                    navigate("/invoice/damaged-report", {
                      state: {
                        store_id: storeId,
                        product_id: row.original.product_id,
                        product_name: row.original.product_name,
                        store_name: storeName,
                      },
                    });
                  }
                }}
                sx={{
                  fontWeight: d > 0 ? 600 : 400,
                  color: d > 0 ? "#1976d2" : "inherit",
                  cursor: d > 0 ? "pointer" : "default",
                  textDecoration: d > 0 ? "underline" : "none",
                  "&:hover": d > 0 ? { opacity: 0.7 } : {},
                }}
              >
                {formatNumber(d)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () =>
          summary?.total_damaged ? (
            <Typography fontWeight={600} sx={{ color: "#e65100" }}>
              {formatNumber(summary.total_damaged)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "missing",
        header: "Missing",
        size: 10,
        enableColumnFilter: false,
        // Flagged via the Missing Stock screen — subtracted from Available
        // above, never from In Stock. See missingStockModel.js.
        Cell: ({ row }) => {
          const m = Number(row.original.missing || 0);
          return (
            <Typography
              fontSize="inherit"
              sx={{ fontWeight: m > 0 ? 600 : 400, color: m > 0 ? "#e65100" : "inherit" }}
            >
              {formatNumber(m)}
            </Typography>
          );
        },
        Footer: () =>
          summary?.total_missing ? (
            <Typography fontWeight={600} sx={{ color: "#e65100" }}>
              {formatNumber(summary.total_missing)}
            </Typography>
          ) : null,
      },
      {
        accessorKey: "expired",
        header: "Expired",
        size: 10,
        enableColumnFilter: false,
        // Still counted in In Stock, but past the expiry date so it can't be sold
        // and is left out of Available.
        Cell: ({ row }) => {
          const e = Number(row.original.expired || 0);
          return (
            <Tooltip title={e > 0 ? "Past expiry date — in stock but not sellable" : ""} arrow>
              <Typography fontSize="inherit" sx={{ fontWeight: e > 0 ? 600 : 400, color: e > 0 ? "#e65100" : "inherit" }}>
                {formatNumber(e)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () => {
          // Backend all-data summary (entire filtered set, not just the page).
          const total = Number(summary?.total_expired) || 0;
          return total ? (
            <Typography fontWeight={600} sx={{ color: "#e65100" }}>{formatNumber(total)}</Typography>
          ) : null;
        },
      },
    ],
    [loading, summary, tblList]
  );

  return (
    <>
      {/* {loading ? (
        <Spinner />
      ) : ( */}
      <Layout>
        <Snackbar
          open={!!successMessage}
          autoHideDuration={3000} // hide after 3 seconds
          onClose={() => setSuccessMessage("")}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSuccessMessage("")}
            severity="success"
            sx={{
              width: "100%",
              backgroundColor: "#28a745",
              color: "#fff",
              fontWeight: 600,
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
        <Grid className="employee-form bold-header">
          <Grid
            container
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Left section */}
            <Grid item xs={12} md={6}>
              <Typography className="page_title" textAlign="left">
                Stock Inventory Report
              </Typography>

              <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                <Link
                  underline="hover"
                  color="inherit"
                  href="/reports/stock-report"
                >
                  Stock Inventory Report
                </Link>
                <Typography color="text.primary">{storeName}</Typography>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12} lg={6} md={6} textAlign="right">
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
                sx={{ ml: 2 }}
              >
                Back
              </Button>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6} lg={4} sx={{ mt: 2 }}>
            {storeId && storeName !== "" && (
              <Typography
                textAlign="left"
                variant="h6"
                sx={{ fontWeight: 600 }}
              >
                {storeName}
              </Typography>
            )}
          </Grid>

          <Grid mt={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MaterialReactTable
              enableGlobalFilter={false}
              enableGlobalFiltering={false}
              columns={Innercolumns}
              data={tblList}
              enableTableFooter
              muiTableFooterProps={{
                sx: {
                  position: "sticky",
                  bottom: 0,
                  zIndex: 100,
                  backgroundColor: "#fff", // match table background
                  boxShadow: "0px -2px 8px rgba(0,0,0,0.1)",
                },
              }}
              muiToolbarAlertBannerProps={{
                sx: {
                  "& .MuiButtonBase-root": {
                    color: "red",
                    fontWeight: "500",
                    width: "100px",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, rgba(255, 153, 153, 0.8) 30%, rgba(255, 187, 187, 0.9) 100%)",
                    },
                  },
                },
              }}
              getRowId={(row) => row.id}
              initialState={{
                showColumnFilters: false,
                columnPinning: { right: ["available"] },
              }}
              enableColumnPinning
              enableRowNumbers
              rowNumberMode="original"
              enableFullScreenToggle={false}
              enableStickyHeader
              manualFiltering
              manualPagination
              manualSorting
              onColumnFiltersChange={setColumnFilters}
              onGlobalFilterChange={setGlobalFilter}
              onPaginationChange={setPagination}
              onSortingChange={setSorting}
              rowCount={rowCount}
              muiLinearProgressProps={{
                color: "inherit",
                sx: { height: "4px" },
              }}
              state={{
                rowSelection: selectedRowIds,
                columnFilters,
                globalFilter,
                isLoading: loading,
                pagination,
                // showAlertBanner: isError,
                // showProgressBars: isRefetching,
                sorting,
              }}
              muiTableContainerProps={{
                sx: {
                  maxHeight: "calc(100vh - 240px)",
                  minHeight: 450,
                  overflowY: "auto",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  padding: "3px 8px",
                  border: "1px solid #ccc",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  padding: "8px",
                  border: "1px solid #ccc",
                  color: "#fff !important",
                  fontWeight: "bold",
                  "& svg": {
                    color: "#fff",
                  },
                  "& .MuiTableSortLabel-root": {
                    color: "#fff !important",
                    "&.Mui-active": {
                      color: "#fff !important",
                      "& .MuiTableSortLabel-icon": {
                        color: "#fff !important",
                      },
                    },
                  },
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  borderBottom: "1px solid #ccc",
                  backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
                },
              })}
              muiTableHeadCellFilterTextFieldProps={{
                sx: {
                  "& input": {
                    color: "#fff !important", // Change text color
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#fff !important", // Change underline color
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff !important", // Hover underline color
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff !important", // Focused underline color
                  },
                },
              }}
              renderTopToolbarCustomActions={({ table }) => (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "1rem",
                      p: "0.5rem",
                      justifyContent: "space-between",
                      flexGrow: 1,

                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* RIGHT SIDE BUTTON */}
                      <Tooltip title="Export data to excel sheet" arrow>
                        <Button
                          onClick={handleExportData}
                          style={{ background: "#1a73e829" }}
                          startIcon={<FileDownload />}
                          // startIcon={<IconTableExport />}
                        >
                          Export To Excel
                        </Button>
                      </Tooltip>
                    </Box>
                  </Box>
                </>
              )}
            />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <ReservedStockDetailsModal
          open={!!reservedDetail}
          onClose={() => setReservedDetail(null)}
          productId={reservedDetail?.product_id}
          storeId={storeId}
          productName={reservedDetail?.product_name}
          storeName={storeName}
        />
        <BatchPriceEditModal
          open={!!priceEditDetail}
          onClose={() => setPriceEditDetail(null)}
          productId={priceEditDetail?.product_id}
          storeId={storeId}
          productName={priceEditDetail?.product_name}
        />
        <BatchSplitModal
          open={!!batchDetail}
          onClose={() => setBatchDetail(null)}
          productId={batchDetail?.product_id}
          storeId={storeId}
          productName={batchDetail?.product_name}
          storeName={storeName}
        />
      </Layout>
    </>
  );
};

export default ProductReports;
